import type {
  StrategyRdCatalog,
  StrategyRdConcept,
  StrategyRdConceptSetting,
  StrategyRdEntryMode,
  StrategyRdMatchMeta,
  StrategyRdOffsetKind,
  StrategyRdOhlcStats,
  StrategyRdPack,
  StrategyRdRuleTemplate,
  StrategyRdSessionOption,
  StrategyRdSessionsPayload,
  StrategyRdStopAnchor,
} from "../types/strategyRd";
import { strategyRdConceptDisplayName } from "./strategyRdConceptCopy";
import { strategyRdImpliedTradeSide } from "./strategyRdComposer";
import { defaultStrategyRdRuleTemplate } from "./strategyRd";
import {
  strategyRdConceptIsLocationInteraction,
  strategyRdLocationIsEvent,
  strategyRdLocationIsZone,
} from "./strategyRdSettings";

/** BE sentinel — expands to all singletons only (not overlaps). */
export const STRATEGY_RD_SESSION_ALL = "all";

/** Older profile chips → current catalog ids. */
const STRATEGY_RD_SESSION_ALIASES: Record<string, string> = {
  newyork: "new_york",
  ny: "new_york",
  london_ny_overlap: "london_new_york",
  london_ny: "london_new_york",
  asia: "tokyo",
};

export function strategyRdCanonicalSessionId(id: string): string {
  const key = id.trim().toLowerCase();
  if (!key) return "";
  return STRATEGY_RD_SESSION_ALIASES[key] || key;
}

export function parseStrategyRdEntryMode(raw: unknown): StrategyRdEntryMode {
  const mode = typeof raw === "string" ? raw.trim() : "";
  if (mode === "signal_open" || mode === "next_open") return "next_open";
  if (
    mode === "break_close" ||
    mode === "retest_then_close" ||
    mode === "rejection" ||
    mode === "limit_at_level" ||
    mode === "signal_close" ||
    mode === "rule"
  ) {
    return mode;
  }
  return "signal_close";
}

export function strategyRdConceptRoleKey(role: string | null | undefined): string {
  return String(role || "").trim().toLowerCase();
}

/** Prefer `roles` when present so dual-role catalog rows appear in both pickers. */
export function strategyRdConceptRoles(
  concept: Pick<StrategyRdConcept, "role" | "roles"> | null | undefined
): string[] {
  const rawRoles = concept?.roles;
  const listed = Array.isArray(rawRoles)
    ? rawRoles.map((role) => strategyRdConceptRoleKey(role)).filter(Boolean)
    : [];
  if (listed.length > 0) return Array.from(new Set(listed));
  const single = strategyRdConceptRoleKey(concept?.role);
  return single ? [single] : [];
}

export function strategyRdConceptHasRole(
  concept: Pick<StrategyRdConcept, "role" | "roles"> | null | undefined,
  role: string
): boolean {
  const want = strategyRdConceptRoleKey(role);
  if (!want) return false;
  return strategyRdConceptRoles(concept).includes(want);
}

/** Stable picker row id — catalog may repeat the same concept id once per role. */
export function strategyRdConceptRowKey(
  concept: Pick<StrategyRdConcept, "id" | "role"> | null | undefined,
  slot?: string | null
): string {
  const id = (concept?.id || "").trim();
  const role = strategyRdConceptRoleKey(slot || concept?.role) || "concept";
  return id ? `${id}:${role}` : role;
}

function mergeConceptSettings(
  a: StrategyRdConceptSetting[] | null | undefined,
  b: StrategyRdConceptSetting[] | null | undefined
): StrategyRdConceptSetting[] | null {
  const rows = [...(a ?? []), ...(b ?? [])];
  if (!rows.length) return null;
  const byKey = new Map<string, StrategyRdConceptSetting>();
  for (const row of rows) {
    const key = (row.key || "").trim();
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, row);
  }
  const merged = Array.from(byKey.values());
  return merged.length > 0 ? merged : null;
}

function pickRicherConcept(a: StrategyRdConcept, b: StrategyRdConcept): StrategyRdConcept {
  const aN = a.settings?.length ?? 0;
  const bN = b.settings?.length ?? 0;
  if (bN > aN) return b;
  if (aN > bN) return a;
  const settings = mergeConceptSettings(a.settings, b.settings);
  return {
    ...a,
    ...b,
    settings: settings ?? a.settings ?? b.settings,
    level_extremes: a.level_extremes === true || b.level_extremes === true ? true : undefined,
    requires_session:
      a.requires_session === true || b.requires_session === true ? true : undefined,
  };
}

/**
 * Dual-role ids are emitted once per role, with different settings.
 * Prefer the row whose `role` matches this picker. Do not union the other
 * role's settings onto it (setup Trade side must not appear on a confluence).
 */
function pickConceptForRoles(
  a: StrategyRdConcept,
  b: StrategyRdConcept,
  preferRoles: ReadonlySet<string> | null
): StrategyRdConcept {
  if (preferRoles && preferRoles.size > 0) {
    const aHit = preferRoles.has(strategyRdConceptRoleKey(a.role));
    const bHit = preferRoles.has(strategyRdConceptRoleKey(b.role));
    if (aHit !== bHit) return bHit ? b : a;
  }
  return pickRicherConcept(a, b);
}

function uniqueConceptsById(
  concepts: readonly StrategyRdConcept[],
  preferRoles?: ReadonlySet<string> | null
): StrategyRdConcept[] {
  const byId = new Map<string, StrategyRdConcept>();
  for (const concept of concepts) {
    const id = concept.id.trim();
    if (!id) continue;
    const prev = byId.get(id);
    byId.set(id, prev ? pickConceptForRoles(prev, concept, preferRoles ?? null) : concept);
  }
  return Array.from(byId.values());
}

/** Best setup row for composer controls (richest settings[] for this id). */
export function strategyRdSetupConceptFromCatalog(
  catalog: StrategyRdCatalog | null | undefined,
  setupId: string | null | undefined
): StrategyRdConcept | null {
  const id = (setupId || "").trim();
  if (!id || !catalog) return null;
  const candidates = catalog.concepts.filter((c) => c.id.trim() === id);
  if (!candidates.length) {
    return strategyRdSetupsFromCatalog(catalog).find((c) => c.id === id) ?? null;
  }
  const preferSetup = new Set(["setup"]);
  let best = candidates[0]!;
  for (let i = 1; i < candidates.length; i += 1) {
    best = pickConceptForRoles(best, candidates[i]!, preferSetup);
  }
  const fromSetups = strategyRdSetupsFromCatalog(catalog).find((c) => c.id === id);
  return fromSetups ? pickConceptForRoles(fromSetups, best, preferSetup) : best;
}

/**
 * Zone / level setups stay in the Setup picker. Nearness uses fvg_overlap /
 * near_ob / near_breaker / clear_of_opposing_sr instead of putting FVG/OB/BB in confluences.
 */
export function strategyRdConceptIsSetupOnlyId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  if (key === "fvg_overlap" || key === "near_ob" || key === "near_breaker" || key.includes("clear_of_opposing")) {
    return false;
  }
  if (key.startsWith("fvg_") || key.includes("fair_value_gap")) return true;
  if (key.includes("order_block") || /(^|_)ob(_|$)/.test(key)) return true;
  // Breaker block setups (agnostic + concretes). near_breaker is location, excluded above.
  if (key === "breaker" || key.startsWith("breaker_")) return true;
  if (key === "pdh_pdl" || key.includes("previous_day")) return true;
  if (key.includes("pdh") && key.includes("pdl")) return true;
  if (key === "pwh_pwl" || (key.includes("pwh") && key.includes("pwl"))) return true;
  if (key === "pmh_pml" || (key.includes("pmh") && key.includes("pml"))) return true;
  if (key === "swing_hl" || key === "swing_high_low") return true;
  return false;
}

/** Flat list of legacy concrete ids (array or `{ long, short }` map). */
export function strategyRdFlattenLegacyIds(
  legacy: StrategyRdConcept["legacy_ids"] | null | undefined
): string[] {
  if (!legacy) return [];
  if (Array.isArray(legacy)) {
    return legacy.map((id) => String(id || "").trim()).filter(Boolean);
  }
  return Object.values(legacy)
    .map((id) => String(id || "").trim())
    .filter(Boolean);
}

type StrategyRdPickerSlot = {
  id: string;
  name?: string | null;
  description?: string | null;
  polarity?: string | null;
  side?: string | null;
  params?: Record<string, unknown> | null;
};

function pickerPolarityEnum(concept: StrategyRdConcept): Set<string> {
  const setting = (concept.settings || []).find((row) => row.key === "polarity");
  return new Set(
    (setting?.enum_values || [])
      .map((row) => String(row.value || "").trim().toLowerCase())
      .filter(Boolean)
  );
}

function polaritySettingIsParam(concept: StrategyRdConcept): boolean {
  const setting = (concept.settings || []).find((row) => row.key === "polarity");
  return setting?.param === true;
}

/** Map a legacy direction key or concrete id onto the parent concept's polarity enum. */
function inferPickerPolarity(
  concept: StrategyRdConcept,
  mapKey: string | null,
  concreteId: string
): string | null {
  const allowed = pickerPolarityEnum(concept);
  const pick = (candidates: string[]): string | null => {
    for (const candidate of candidates) {
      if (allowed.size === 0 || allowed.has(candidate)) return candidate;
    }
    return null;
  };
  if (mapKey === "long") {
    const fromKey = pick(["bull", "above", "high"]);
    if (fromKey) return fromKey;
  }
  if (mapKey === "short") {
    const fromKey = pick(["bear", "below", "low"]);
    if (fromKey) return fromKey;
  }
  const id = concreteId.trim().toLowerCase();
  if (/(^|_)bull($|_)/.test(id) || id.startsWith("bull") || id.includes("bullish")) {
    return pick(["bull"]) || "bull";
  }
  if (/(^|_)bear($|_)/.test(id) || id.startsWith("bear") || id.includes("bearish")) {
    return pick(["bear"]) || "bear";
  }
  if (id.startsWith("above_") || id.includes("_above")) return pick(["above"]) || "above";
  if (id.startsWith("below_") || id.includes("_below")) return pick(["below"]) || "below";
  return null;
}

function inferPickerLength(concept: StrategyRdConcept, concreteId: string): number | null {
  const hasLength = (concept.settings || []).some((row) => row.key === "length");
  if (!hasLength) return null;
  const match = concreteId.trim().toLowerCase().match(/(?:sma|ema)(\d+)$/);
  if (!match) return null;
  const length = Number(match[1]);
  return Number.isFinite(length) ? length : null;
}

function findPickerParent(
  catalog: StrategyRdCatalog,
  rawId: string
): { concept: StrategyRdConcept; mapKey: string | null } | null {
  for (const concept of catalog.concepts) {
    if (strategyRdConceptHiddenFromPicker(concept)) continue;
    const legacy = concept.legacy_ids;
    if (!legacy) continue;
    if (Array.isArray(legacy)) {
      if (legacy.some((id) => String(id || "").trim() === rawId)) {
        return { concept, mapKey: null };
      }
      continue;
    }
    for (const [key, id] of Object.entries(legacy)) {
      if (String(id || "").trim() === rawId) return { concept, mapKey: key };
    }
  }
  const concrete = catalog.concepts.find((concept) => concept.id.trim() === rawId);
  const superseded = String(concrete?.superseded_by || "").trim();
  if (!superseded) return null;
  const parent = catalog.concepts.find(
    (concept) => concept.id.trim() === superseded && !strategyRdConceptHiddenFromPicker(concept)
  );
  return parent ? { concept: parent, mapKey: null } : null;
}

/**
 * Concrete catalog ids (`fvg_bull`) are hidden from the picker.
 * Select the official family row (`fvg` / Fair value gap) and set polarity.
 */
export function strategyRdRewriteSlotToPicker<T extends StrategyRdPickerSlot>(
  catalog: StrategyRdCatalog | null | undefined,
  slot: T
): T {
  const rawId = String(slot?.id || "").trim();
  if (!rawId || !catalog) return slot;

  const visible = catalog.concepts.find(
    (concept) => concept.id.trim() === rawId && !strategyRdConceptHiddenFromPicker(concept)
  );
  if (visible) {
    const name = (visible.name || "").trim();
    if (name && (!slot.name || slot.name === rawId)) {
      return {
        ...slot,
        name,
        description: slot.description || visible.description || null,
      };
    }
    return slot;
  }

  const parent = findPickerParent(catalog, rawId);
  if (!parent) return slot;

  const next: T = {
    ...slot,
    id: parent.concept.id,
    name: (parent.concept.name || "").trim() || slot.name,
    description: (parent.concept.description || "").trim() || slot.description || null,
  };
  const polarity =
    (slot.polarity != null && String(slot.polarity).trim()) ||
    inferPickerPolarity(parent.concept, parent.mapKey, rawId);
  if (polarity) {
    if (polaritySettingIsParam(parent.concept)) {
      const params = { ...(slot.params || {}) };
      if (params.polarity == null || params.polarity === "") params.polarity = polarity;
      next.params = params;
    } else if (slot.polarity == null || !String(slot.polarity).trim()) {
      next.polarity = polarity;
    }
  }
  const length = inferPickerLength(parent.concept, rawId);
  if (length != null) {
    const params = { ...(next.params || {}) };
    if (params.length == null || params.length === "") params.length = length;
    next.params = params;
  }
  const implied = strategyRdImpliedTradeSide(rawId, parent.mapKey);
  if (implied) {
    const current = (next.side || "").trim().toLowerCase();
    if (current !== "long" && current !== "short" && current !== "invert") {
      next.side = implied;
    }
  }
  return next;
}

/** Hide from Setup / Confluence pickers when BE marks picker_hidden or superseded_by. */
export function strategyRdConceptHiddenFromPicker(
  concept:
    | Pick<StrategyRdConcept, "picker_hidden" | "superseded_by">
    | null
    | undefined
): boolean {
  if (!concept) return false;
  if (concept.picker_hidden === true) return true;
  return Boolean(String(concept.superseded_by || "").trim());
}

export function strategyRdSetupsFromCatalog(catalog: StrategyRdCatalog | null): StrategyRdConcept[] {
  if (!catalog) return [];
  // Trust BE default /concepts list — hide only when picker_hidden / superseded_by.
  return uniqueConceptsById(
    catalog.concepts.filter(
      (concept) =>
        strategyRdConceptHasRole(concept, "setup") &&
        !strategyRdConceptHiddenFromPicker(concept)
    ),
    new Set(["setup"])
  );
}

/** Setup picker column id — catalog family (+ candle bar_span), not a hardcoded concept list. */
export type StrategyRdSetupPickerGroupId =
  | "condition"
  | "level"
  | "structure"
  | "candle"
  | "candle_multi"
  | "chart"
  | "momentum"
  | "ma"
  | "volatility"
  | "relative_strength"
  | "other";

const SETUP_PICKER_GROUP_ORDER: readonly StrategyRdSetupPickerGroupId[] = [
  "condition",
  "candle",
  "candle_multi",
  "chart",
  "structure",
  "level",
  "ma",
  "relative_strength",
  "volatility",
  "momentum",
  "other",
];

const SETUP_PICKER_GROUP_LABELS: Record<StrategyRdSetupPickerGroupId, string> = {
  condition: "Indicator conditions",
  level: "Levels",
  structure: "Structure",
  candle: "Candles",
  candle_multi: "Multi-candle",
  chart: "Chart patterns",
  momentum: "Momentum",
  ma: "Moving averages",
  volatility: "Volatility",
  relative_strength: "Relative strength",
  other: "Other",
};

const STRUCTURE_FAMILIES = new Set([
  "ict",
  "fvg",
  "order_block",
  "breaker",
  "liquidity",
  "coil",
  "structure",
  "bos",
  "choch",
]);

/** Map a catalog setup row → picker column (family / bar_span driven). */
export function strategyRdSetupPickerGroupId(
  concept:
    | Pick<StrategyRdConcept, "family" | "family_kind" | "bar_span" | "id">
    | null
    | undefined
): StrategyRdSetupPickerGroupId {
  const family = String(concept?.family || "")
    .trim()
    .toLowerCase();
  const familyKind = String(concept?.family_kind || "")
    .trim()
    .toLowerCase();
  const barSpan = String(concept?.bar_span || "")
    .trim()
    .toLowerCase();
  const id = String(concept?.id || "")
    .trim()
    .toLowerCase();

  if (family === "condition" || id === "condition") return "condition";
  if (family === "stage" || familyKind === "stage" || id === "stage" || /^stage_\d+$/.test(id)) {
    return "structure";
  }

  if (family === "candle") {
    if (barSpan === "double" || barSpan === "triple" || barSpan === "multi") {
      return "candle_multi";
    }
    // Agnostic multi-bar ids when bar_span omitted.
    if (
      id.includes("harami") ||
      id.includes("tweezer") ||
      id.includes("star") ||
      id.includes("three_") ||
      id.includes("piercing") ||
      id.includes("dark_cloud") ||
      id.includes("cloud_pierce") ||
      id.includes("engulf") ||
      id.includes("inside_bar")
    ) {
      // engulf / inside are often single/double — prefer bar_span; without it keep Candles
      // except known multi-bar families.
      if (
        id.includes("harami") ||
        id.includes("tweezer") ||
        id.includes("morning_star") ||
        id.includes("evening_star") ||
        id.includes("star_triple") ||
        id.includes("three_") ||
        id.includes("piercing") ||
        id.includes("dark_cloud") ||
        id.includes("cloud_pierce")
      ) {
        return "candle_multi";
      }
    }
    return "candle";
  }
  if (family === "chart") return "chart";
  if (
    family === "level" ||
    family === "session" ||
    family === "orb" ||
    id.includes("session_first") ||
    id.includes("pdh") ||
    id.includes("pdl") ||
    id.includes("pwh") ||
    id.includes("pwl") ||
    id.includes("pmh") ||
    id.includes("pml") ||
    id === "swing_hl" ||
    id.includes("swing_high_low") ||
    id.includes("developing_day") ||
    id.includes("opening_range")
  ) {
    return "level";
  }
  if (STRUCTURE_FAMILIES.has(family) || id.includes("coil")) return "structure";
  if (family === "momentum") return "momentum";
  if (family === "ma") return "ma";
  if (family === "volatility") return "volatility";
  if (family === "relative_strength") return "relative_strength";
  return "other";
}

export type StrategyRdSetupPickerGroup = {
  id: StrategyRdSetupPickerGroupId;
  label: string;
  concepts: StrategyRdConcept[];
};

/** Group catalog setups into ordered picker columns (omit empty columns). */
export function strategyRdSetupPickerGroups(
  catalog: StrategyRdCatalog | null
): StrategyRdSetupPickerGroup[] {
  const setups = strategyRdSetupsFromCatalog(catalog);
  const buckets = new Map<StrategyRdSetupPickerGroupId, StrategyRdConcept[]>();
  for (const id of SETUP_PICKER_GROUP_ORDER) buckets.set(id, []);
  for (const concept of setups) {
    const gid = strategyRdSetupPickerGroupId(concept);
    buckets.get(gid)!.push(concept);
  }
  return SETUP_PICKER_GROUP_ORDER.filter((id) => (buckets.get(id)?.length ?? 0) > 0).map(
    (id) => ({
      id,
      label: SETUP_PICKER_GROUP_LABELS[id],
      concepts: buckets.get(id)!,
    })
  );
}

const CONFLUENCE_TABLE_ROLES = new Set(["confluence", "confirmation", "exit_helper"]);

/** Catalog filters shown in the Confluence table (not setups). */
export function strategyRdIsConfluenceConcept(
  concept: Pick<StrategyRdConcept, "id" | "role" | "roles"> | null | undefined
): boolean {
  if (!concept) return false;
  if (strategyRdConceptIsSetupOnlyId(concept.id)) return false;
  return strategyRdConceptRoles(concept).some((role) => CONFLUENCE_TABLE_ROLES.has(role));
}

export function strategyRdConfluencesFromCatalog(
  catalog: StrategyRdCatalog | null
): StrategyRdConcept[] {
  if (!catalog) return [];
  // Trust BE default list — hide only when picker_hidden / superseded_by.
  const rows = uniqueConceptsById(
    catalog.concepts.filter(
      (concept) =>
        strategyRdIsConfluenceConcept(concept) &&
        !strategyRdConceptHiddenFromPicker(concept)
    ),
    CONFLUENCE_TABLE_ROLES
  );
  return [...rows].sort((a, b) => {
    const aMa = String(a.family || "").trim().toLowerCase() === "ma" ? 0 : 1;
    const bMa = String(b.family || "").trim().toLowerCase() === "ma" ? 0 : 1;
    return aMa - bMa;
  });
}

/** Confluence picker column — location vs context family. */
export type StrategyRdConfluencePickerGroupId =
  | "location_zone"
  | "location_event"
  | "location"
  | "ma"
  | "structure"
  | "volume"
  | "momentum"
  | "volatility"
  | "relative_strength"
  | "time"
  | "context";

const CONFLUENCE_PICKER_GROUP_ORDER: readonly StrategyRdConfluencePickerGroupId[] = [
  "structure",
  "location_zone",
  "location_event",
  "volume",
  "ma",
  "time",
  "relative_strength",
  "volatility",
  "momentum",
  "location",
  "context",
];

const CONFLUENCE_PICKER_GROUP_LABELS: Record<StrategyRdConfluencePickerGroupId, string> = {
  location_zone: "Location · Zone",
  location_event: "Location · Event",
  location: "Location",
  ma: "Moving averages",
  structure: "Structure",
  volume: "Volume",
  momentum: "Momentum",
  volatility: "Volatility",
  relative_strength: "Relative strength",
  time: "Time",
  context: "Context",
};

export function strategyRdConfluencePickerGroupId(
  concept: Pick<StrategyRdConcept, "id" | "family" | "location_interaction"> | null | undefined
): StrategyRdConfluencePickerGroupId {
  const id = String(concept?.id || "")
    .trim()
    .toLowerCase();
  const family = String(concept?.family || "")
    .trim()
    .toLowerCase();

  if (strategyRdConceptIsLocationInteraction(concept)) {
    if (strategyRdLocationIsZone(concept?.id)) return "location_zone";
    if (strategyRdLocationIsEvent(concept?.id)) return "location_event";
    return "location";
  }

  if (family === "ma" || /(^|_)sma($|_)/.test(id) || /(^|_)ema($|_)/.test(id)) return "ma";
  if (family === "stage" || id === "stage" || /^stage_\d+$/.test(id)) return "structure";
  if (family === "volume" || id.includes("vol_") || id.includes("rvol") || id.includes("volume")) {
    return "volume";
  }
  if (
    family === "momentum" ||
    /(^|_)rsi($|_)/.test(id) ||
    /(^|_)macd($|_)/.test(id) ||
    /(^|_)stoch($|_)/.test(id) ||
    /(^|_)mfi($|_)/.test(id) ||
    /(^|_)adx($|_)/.test(id)
  ) {
    return "momentum";
  }
  if (
    family === "volatility" ||
    id.includes("atr") ||
    id.includes("bb_") ||
    id.includes("bollinger") ||
    id.includes("donchian") ||
    id.includes("ttm") ||
    id.includes("squeeze")
  ) {
    return "volatility";
  }
  if (
    family === "relative_strength" ||
    id.includes("mansfield") ||
    id.includes("corr_rs") ||
    id.includes("rs_vs") ||
    id.includes("own_momentum")
  ) {
    return "relative_strength";
  }
  if (family === "time" || id.includes("killzone") || id.includes("session")) return "time";
  if (
    STRUCTURE_FAMILIES.has(family) ||
    id.includes("coil") ||
    id.includes("htf_bias") ||
    id.includes("regime") ||
    id.includes("clear_of_opposing") ||
    id.includes("inside_bar")
  ) {
    return "structure";
  }
  return "context";
}

export type StrategyRdConfluencePickerGroup = {
  id: StrategyRdConfluencePickerGroupId;
  label: string;
  concepts: StrategyRdConcept[];
};

/** Group confluence concepts into ordered picker columns (omit empty). */
export function strategyRdConfluencePickerGroups(
  concepts: readonly StrategyRdConcept[] | null | undefined
): StrategyRdConfluencePickerGroup[] {
  const buckets = new Map<StrategyRdConfluencePickerGroupId, StrategyRdConcept[]>();
  for (const id of CONFLUENCE_PICKER_GROUP_ORDER) buckets.set(id, []);
  for (const concept of concepts ?? []) {
    const gid = strategyRdConfluencePickerGroupId(concept);
    buckets.get(gid)!.push(concept);
  }
  return CONFLUENCE_PICKER_GROUP_ORDER.filter((id) => (buckets.get(id)?.length ?? 0) > 0).map(
    (id) => ({
      id,
      label: CONFLUENCE_PICKER_GROUP_LABELS[id],
      concepts: buckets.get(id)!,
    })
  );
}

export function strategyRdPacksForWave(
  catalog: StrategyRdCatalog | null,
  wave: string | null
): StrategyRdPack[] {
  if (!catalog) return [];
  if (!wave || wave === "all") return catalog.packs;
  return catalog.packs.filter((p) => String(p.wave).toUpperCase() === wave.toUpperCase());
}

/** Concept-type filter for composer (setups + packs). Kept for tests / legacy callers. */
export type StrategyRdConceptFamilyFilter = "all" | "candle" | "ict" | "level";

export const STRATEGY_RD_CONCEPT_FAMILY_FILTER_OPTIONS: Array<{
  value: StrategyRdConceptFamilyFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "candle", label: "Candles" },
  { value: "ict", label: "ICT" },
  { value: "level", label: "Level" },
];

export function strategyRdConceptMatchesFamilyFilter(
  concept: Pick<StrategyRdConcept, "family"> | null | undefined,
  filter: StrategyRdConceptFamilyFilter
): boolean {
  if (!filter || filter === "all") return true;
  return String(concept?.family || "")
    .trim()
    .toLowerCase() === filter;
}

export function strategyRdSetupsForFamilyFilter(
  catalog: StrategyRdCatalog | null,
  filter: StrategyRdConceptFamilyFilter
): StrategyRdConcept[] {
  return strategyRdSetupsFromCatalog(catalog).filter((c) =>
    strategyRdConceptMatchesFamilyFilter(c, filter)
  );
}

export function strategyRdPacksForFamilyFilter(
  catalog: StrategyRdCatalog | null,
  filter: StrategyRdConceptFamilyFilter
): StrategyRdPack[] {
  if (!catalog) return [];
  if (!filter || filter === "all") return catalog.packs;
  const setupFamilyById = new Map(
    strategyRdSetupsFromCatalog(catalog).map((c) => [c.id, c.family] as const)
  );
  return catalog.packs.filter((pack) =>
    strategyRdConceptMatchesFamilyFilter(
      { family: setupFamilyById.get(pack.setup_id) || "" },
      filter
    )
  );
}

export function strategyRdSessionsFromCatalog(
  catalog: StrategyRdCatalog | null | undefined
): StrategyRdSessionOption[] {
  const payload = catalog?.sessions;
  if (!payload?.sessions?.length) return [];
  return [...payload.sessions].sort((a, b) => {
    const aOverlap = a.kind === "overlap" ? 1 : 0;
    const bOverlap = b.kind === "overlap" ? 1 : 0;
    if (aOverlap !== bOverlap) return aOverlap - bOverlap;
    return (a.ui_sort_order ?? 0) - (b.ui_sort_order ?? 0);
  });
}

export function strategyRdAllSessionSentinel(
  catalog: StrategyRdCatalog | null | undefined
): string {
  const raw = catalog?.sessions?.all_sentinel?.trim();
  return raw || STRATEGY_RD_SESSION_ALL;
}

/** Title-case a session id fallback (`new_york` → `New York`). */
export function strategyRdSessionIdLabel(id: string): string {
  return id
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function strategyRdSessionOptionLabel(session: StrategyRdSessionOption): string {
  const name = (session.name || "").trim();
  if (name) return name;
  const base = strategyRdSessionIdLabel(session.id);
  if (session.kind === "overlap" && !/overlap/i.test(base)) {
    return `${base} overlap`;
  }
  return base;
}

export type StrategyRdSessionPickerGroup = {
  id: "all" | "singleton" | "overlap";
  label: string;
  options: Array<{ value: string; label: string }>;
};

/** All from `all_sentinel`, then `sessions[]` split by `kind`. */
export function strategyRdSessionPickerGroups(
  catalog: StrategyRdCatalog | null | undefined
): StrategyRdSessionPickerGroup[] {
  const allId = strategyRdAllSessionSentinel(catalog);
  const rows = strategyRdSessionsFromCatalog(catalog).filter((s) => s.id !== allId);
  const toOption = (s: StrategyRdSessionOption) => ({
    value: s.id,
    label: strategyRdSessionOptionLabel(s),
  });
  const singletons = rows.filter((s) => s.kind !== "overlap").map(toOption);
  const overlaps = rows.filter((s) => s.kind === "overlap").map(toOption);
  const groups: StrategyRdSessionPickerGroup[] = [
    {
      id: "all",
      label: "All",
      options: [{ value: allId, label: "All sessions" }],
    },
  ];
  if (singletons.length > 0) {
    groups.push({ id: "singleton", label: "Sessions", options: singletons });
  }
  if (overlaps.length > 0) {
    groups.push({ id: "overlap", label: "Overlaps", options: overlaps });
  }
  return groups;
}

/** Flat picker list: All, then singletons, then overlaps. */
export function strategyRdSessionSelectOptions(
  catalog: StrategyRdCatalog | null | undefined
): Array<{ value: string; label: string }> {
  return strategyRdSessionPickerGroups(catalog).flatMap((group) => group.options);
}

export function strategyRdSessionDisplayName(
  catalog: StrategyRdCatalog | null | undefined,
  sessionId: string | null | undefined
): string {
  const id = (sessionId || "").trim();
  if (!id) return "";
  const allId = strategyRdAllSessionSentinel(catalog);
  if (id === allId) return "All sessions";
  const hit = strategyRdSessionsFromCatalog(catalog).find((s) => s.id === id);
  if (hit) return strategyRdSessionOptionLabel(hit);
  return strategyRdSessionIdLabel(id);
}

export function strategyRdMatchSessionId(
  meta: StrategyRdMatchMeta | null | undefined
): string | null {
  if (!meta) return null;
  const sid = meta.session_id;
  if (typeof sid === "string" && sid.trim()) return sid.trim();
  const multi = meta.session_ids;
  if (Array.isArray(multi) && multi.length > 0) {
    const first = multi[0];
    if (typeof first === "string" && first.trim()) return first.trim();
  }
  return null;
}

/**
 * Normalize multi-select. `all` is exclusive.
 * Choosing a session while All is on drops All and keeps that session.
 * Choosing All while other sessions are on keeps only All.
 * Empty → london when a selection is required.
 */
export function normalizeStrategyRdSessionIdsSelection(
  next: string[],
  opts?: { requireNonEmpty?: boolean; allSentinel?: string; previous?: readonly string[] }
): string[] {
  const allId = (opts?.allSentinel || STRATEGY_RD_SESSION_ALL).trim() || STRATEGY_RD_SESSION_ALL;
  const cleaned = next
    .map((s) => strategyRdCanonicalSessionId(String(s)))
    .filter(Boolean);
  const prev = (opts?.previous || [])
    .map((s) => strategyRdCanonicalSessionId(String(s)))
    .filter(Boolean);
  const nextHasAll = cleaned.includes(allId);
  const prevHadAll = prev.includes(allId);
  if (nextHasAll && prevHadAll) {
    const picked = Array.from(new Set(cleaned.filter((id) => id !== allId)));
    if (picked.length > 0) return picked;
    return [allId];
  }
  if (nextHasAll) return [allId];
  const unique = Array.from(new Set(cleaned));
  // Nothing picked = every hour (no session filter).
  if (unique.length === 0 && opts?.requireNonEmpty !== false) return [allId];
  return unique;
}

export function strategyRdSetupNeedsSession(
  setupId: string | null | undefined,
  catalog?: StrategyRdCatalog | null
): boolean {
  const id = (setupId || "").trim();
  if (!id) return false;
  if (catalog) {
    const concept = catalog.concepts.find((c) => c.id === id);
    if (concept?.requires_session === true) return true;
    if (concept?.requires_session === false) return false;
  }
  return id === "session_first_candle";
}

export type StrategyRdConfluenceSkipEntry = { id: string; reason: string };

export function strategyRdParseConfluenceSkipped(
  raw: unknown
): StrategyRdConfluenceSkipEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdConfluenceSkipEntry[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const id = item.trim();
      if (id) out.push({ id, reason: id });
      continue;
    }
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item as Record<string, unknown>;
    const id = String(row.id ?? "").trim();
    const reason = String(row.reason ?? "").trim();
    if (id || reason) out.push({ id: id || reason, reason: reason || id });
  }
  return out;
}

export function strategyRdConfluenceSkipDisplay(entry: StrategyRdConfluenceSkipEntry): string {
  const reason = entry.reason.trim().toLowerCase();
  const id = entry.id.trim().toLowerCase();
  if (reason === "volume_unavailable" || id === "volume_unavailable") {
    return "Volume filter skipped (no volume)";
  }
  if (entry.id && entry.reason && entry.id !== entry.reason) {
    return `${entry.id}: ${entry.reason}`;
  }
  return entry.reason || entry.id;
}

export function strategyRdVolumeUnavailable(
  skipped: string[] | null | undefined
): boolean {
  return (skipped ?? []).some((value) => value.includes("volume_unavailable"));
}

/**
 * Runtime volume availability from preview/scan ohlc_stats.
 * - `true` / `false` from `volume_available` or `volume_status.available`
 * - else infer `false` when volume chips were soft-skipped
 * - else `null` (unknown — do not assume from asset class)
 */
export function strategyRdVolumeAvailableFromStats(
  ohlcStats: StrategyRdOhlcStats | null | undefined
): boolean | null {
  if (!ohlcStats) return null;
  if (typeof ohlcStats.volume_available === "boolean") {
    return ohlcStats.volume_available;
  }
  const status = ohlcStats.volume_status;
  if (status && typeof status.available === "boolean") {
    return status.available;
  }
  if (strategyRdVolumeUnavailable(strategyRdConfluenceSkippedReasons(ohlcStats))) {
    return false;
  }
  return null;
}

/** Hide/disable volume_requires chips only when runtime says volume is missing. */
export function strategyRdVolumeChipsBlocked(
  ohlcStats: StrategyRdOhlcStats | null | undefined
): boolean {
  return strategyRdVolumeAvailableFromStats(ohlcStats) === false;
}

export function strategyRdConfluenceSkippedReasons(
  ohlcStats: { confluences?: { skipped?: unknown } | null } | null | undefined
): string[] {
  return strategyRdParseConfluenceSkipped(ohlcStats?.confluences?.skipped).map(
    (entry) => entry.reason || entry.id
  );
}

export function strategyRdConfluenceSkippedDisplay(
  ohlcStats: { confluences?: { skipped?: unknown } | null } | null | undefined
): string[] {
  return strategyRdParseConfluenceSkipped(ohlcStats?.confluences?.skipped).map(
    strategyRdConfluenceSkipDisplay
  );
}

export function strategyRdConfluenceSoftPassNotes(
  ohlcStats: { confluences?: { notes?: unknown } | null } | null | undefined
): string[] {
  const notes = ohlcStats?.confluences?.notes;
  if (!Array.isArray(notes)) return [];
  const out: string[] = [];
  for (const item of notes) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item as Record<string, unknown>;
    const id = String(row.id ?? "").trim();
    const soft = String(row.soft_pass ?? "").trim();
    if (!id && !soft) continue;
    const name = id ? strategyRdConceptDisplayName(id) : "Confluence";
    out.push(soft ? `${name}: gate soft-passed (${soft})` : `${name}: gate soft-passed`);
  }
  return out;
}

/** BE `diag.dropped` reason → funnel copy. Unknown reasons fall back to a generic line. */
const STRATEGY_RD_DROPPED_REASON_COPY: Record<string, string> = {
  trendline_fit_missing: "no trendline could be fitted before the setup",
  already_broken: "price had already broken the line before the setup",
  swing_not_found: "no swing high/low qualified near the setup",
  levels_missing: "the prior day/week/month high and low were not available",
  prior_sr_not_near: "price was not near the prior high or low",
  fvg_not_found: "no fair value gap qualified near the setup",
  ob_not_found: "no order block qualified near the setup",
  breaker_not_found: "no breaker block qualified near the setup",
  sweep_not_found: "no liquidity sweep before the setup or within Max wait after it",
  awaiting_sweep: "no liquidity sweep before the setup or within Max wait after it",
  run_not_found: "no liquidity run before the setup or within Max wait after it",
  awaiting_run: "no liquidity run before the setup or within Max wait after it",
  bos_not_found: "no BOS before the setup or within Max wait after it",
  choch_not_found: "no CHoCH before the setup or within Max wait after it",
  bos_reject_not_confirmed: "the opposing break never failed (no close back through its level)",
  choch_reject_not_confirmed: "the opposing break never failed (no close back through its level)",
  prior_liq_sweep_reject_not_confirmed:
    "the opposing sweep never failed (no close back beyond its wick)",
  prior_liq_run_reject_not_confirmed:
    "the opposing run never failed (no close back through the pool it broke)",
  entry_wait_exceeded: "entry wait exceeded (nothing filled within Max wait of the setup close)",
};

export type StrategyRdConfluenceDropped = {
  slotKey: string;
  reason: string;
  count: number;
};

function droppedFromDiag(
  diag: unknown,
  into: Map<string, StrategyRdConfluenceDropped>
): void {
  if (!diag || typeof diag !== "object" || Array.isArray(diag)) return;
  const dropped = (diag as Record<string, unknown>).dropped;
  if (!dropped || typeof dropped !== "object" || Array.isArray(dropped)) return;
  for (const [slotKey, reasons] of Object.entries(dropped as Record<string, unknown>)) {
    if (!reasons || typeof reasons !== "object" || Array.isArray(reasons)) continue;
    for (const [reason, rawCount] of Object.entries(reasons as Record<string, unknown>)) {
      const count = Number(rawCount);
      if (!Number.isFinite(count) || count <= 0) continue;
      const key = `${slotKey}\u0000${reason}`;
      const prev = into.get(key);
      if (prev) prev.count += count;
      else into.set(key, { slotKey, reason, count });
    }
  }
}

/**
 * Setups removed by location confluences that found nothing
 * (`ohlc_stats.confluences.by_direction.<long|short>.diag.dropped`, and
 * `…neutral.by_side.<long|short>.diag.dropped` for neutral setups). Summed per slot + reason.
 */
export function strategyRdConfluenceDropped(
  ohlcStats: { confluences?: Record<string, unknown> | null } | null | undefined
): StrategyRdConfluenceDropped[] {
  const byDirection = ohlcStats?.confluences?.by_direction;
  if (!byDirection || typeof byDirection !== "object" || Array.isArray(byDirection)) return [];
  const acc = new Map<string, StrategyRdConfluenceDropped>();
  for (const [direction, block] of Object.entries(byDirection as Record<string, unknown>)) {
    if (!block || typeof block !== "object" || Array.isArray(block)) continue;
    const row = block as Record<string, unknown>;
    droppedFromDiag(row.diag, acc);
    if (direction === "neutral") {
      const bySide = row.by_side;
      if (bySide && typeof bySide === "object" && !Array.isArray(bySide)) {
        for (const side of Object.values(bySide as Record<string, unknown>)) {
          if (side && typeof side === "object" && !Array.isArray(side)) {
            droppedFromDiag((side as Record<string, unknown>).diag, acc);
          }
        }
      }
    }
  }
  return Array.from(acc.values()).sort((a, b) => b.count - a.count);
}

/** Chip name for a BE slot key (`near_ob`, `near_ob#2` → "Near order block (2)"). */
export function strategyRdSlotKeyDisplayName(slotKey: string): string {
  const [base, nth] = slotKey.split("#", 2);
  const name = strategyRdConceptDisplayName((base || "").trim());
  return nth && nth.trim() ? `${name} (${nth.trim()})` : name;
}

export function strategyRdConfluenceDroppedDisplay(
  ohlcStats: { confluences?: Record<string, unknown> | null } | null | undefined
): string[] {
  return strategyRdConfluenceDropped(ohlcStats).map(({ slotKey, reason, count }) => {
    const name = strategyRdSlotKeyDisplayName(slotKey);
    const noun = count === 1 ? "setup" : "setups";
    const why = STRATEGY_RD_DROPPED_REASON_COPY[reason];
    return why ? `${name} removed ${count} ${noun}: ${why}.` : `${name} removed ${count} ${noun}.`;
  });
}

export function strategyRdIsVolumeConfluence(concept: StrategyRdConcept): boolean {
  return concept.volume_requires === true || concept.family === "volume";
}

/** Catalog id that belongs on the volume histogram (spike, RVOL, expansion, dry-up). */
export function strategyRdConceptIdUsesVolume(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  return (
    key.includes("volume") ||
    key.includes("rvol") ||
    key.includes("vol_expand") ||
    key.includes("vol_dry") ||
    key.includes("dry_up")
  );
}

/** Setup or confluence that should open the volume pane when the series has volume. */
export function strategyRdSlotUsesVolume(
  slot: { id?: string | null; params?: Record<string, unknown> | null } | null | undefined
): boolean {
  if (!slot) return false;
  if (strategyRdConceptIdUsesVolume(slot.id)) return true;
  const flag = slot.params?.require_vol_expand;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
}

export const STRATEGY_RD_CLEAR_OF_OPPOSING_SR_ID = "clear_of_opposing_sr";

export const STRATEGY_RD_SR_SCOPES = ["day", "week", "month"] as const;
export type StrategyRdSrScope = (typeof STRATEGY_RD_SR_SCOPES)[number];

export const STRATEGY_RD_SR_SCOPE_OPTIONS: Array<{ value: StrategyRdSrScope; label: string }> = [
  { value: "day", label: "Day (PDH/PDL)" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const SR_SCOPE_ALIASES: Record<string, StrategyRdSrScope> = {
  day: "day",
  daily: "day",
  week: "week",
  weekly: "week",
  month: "month",
  monthly: "month",
};

export function strategyRdHasClearOfOpposingSr(
  confluences: readonly string[] | null | undefined
): boolean {
  return (confluences ?? []).some(
    (id) => id.trim().toLowerCase() === STRATEGY_RD_CLEAR_OF_OPPOSING_SR_ID
  );
}

export function normalizeStrategyRdSrScopes(
  raw: readonly unknown[] | null | undefined
): StrategyRdSrScope[] {
  const seen = new Set<StrategyRdSrScope>();
  for (const item of raw ?? []) {
    const canonical = SR_SCOPE_ALIASES[String(item ?? "").trim().toLowerCase()];
    if (canonical) seen.add(canonical);
  }
  return STRATEGY_RD_SR_SCOPES.filter((scope) => seen.has(scope));
}

/** Omit when the confluence is off, or when empty/all-three (BE default). */
export function strategyRdSrScopesForRequest(
  selected: readonly unknown[] | null | undefined,
  confluences: readonly string[] | null | undefined
): StrategyRdSrScope[] | undefined {
  if (!strategyRdHasClearOfOpposingSr(confluences)) return undefined;
  const scopes = normalizeStrategyRdSrScopes(selected);
  if (scopes.length === 0 || scopes.length === STRATEGY_RD_SR_SCOPES.length) return undefined;
  return scopes;
}

/** Chart: empty/all-three still draws every scope; off draws nothing. */
export function strategyRdSrScopesForChart(
  selected: readonly unknown[] | null | undefined,
  confluences: readonly string[] | null | undefined
): StrategyRdSrScope[] {
  if (!strategyRdHasClearOfOpposingSr(confluences)) return [];
  const scopes = normalizeStrategyRdSrScopes(selected);
  return scopes.length > 0 ? scopes : [...STRATEGY_RD_SR_SCOPES];
}

export function strategyRdConceptRecipeFingerprint(input: {
  setupId: string;
  sessionIds: string[];
  confluences: string[];
  srScopes?: string[] | null;
}): string {
  const confluences = [...input.confluences].map((s) => s.trim()).filter(Boolean).sort();
  const srOn = strategyRdHasClearOfOpposingSr(confluences);
  const srScopes = srOn
    ? strategyRdSrScopesForRequest(input.srScopes, confluences) ?? [...STRATEGY_RD_SR_SCOPES]
    : [];
  return JSON.stringify({
    setupId: input.setupId,
    sessionIds: [...input.sessionIds].map((s) => s.trim().toLowerCase()).filter(Boolean).sort(),
    confluences,
    srScopes,
  });
}

/** Apply pack recipe into composer + rules template (user can unbundle after). */
export function applyStrategyRdPackRecipe(
  pack: StrategyRdPack,
  currentTemplate?: StrategyRdRuleTemplate | null
): {
  setupId: string;
  confluences: string[];
  sessionIds: string[];
  template: StrategyRdRuleTemplate;
} {
  const base = currentTemplate ?? defaultStrategyRdRuleTemplate();
  const entryMode = parseStrategyRdEntryMode(pack.entry_mode);
  let template: StrategyRdRuleTemplate = {
    ...base,
    entry: { mode: entryMode },
  };
  const stopKind = (pack.stop_offset_kind || "").trim();
  if (stopKind === "beyond_level") {
    template = {
      ...template,
      stop: {
        anchor: "level_extreme" as StrategyRdStopAnchor,
        offset: { kind: "beyond_level" as StrategyRdOffsetKind, value: 0 },
      },
    };
  }
  const needsSession = strategyRdSetupNeedsSession(pack.setup_id);
  const packSessions = Array.isArray(pack.session_ids)
    ? pack.session_ids.map((s) => String(s).trim().toLowerCase()).filter(Boolean)
    : [];
  const sessionIds = needsSession
    ? normalizeStrategyRdSessionIdsSelection(
        packSessions.length > 0 ? packSessions : ["london"]
      )
    : [];
  return {
    setupId: pack.setup_id,
    confluences: [...pack.confluences],
    sessionIds,
    template,
  };
}

export type StrategyRdMetaZone = {
  high: number;
  low: number;
  label: string;
  mid: number;
};

function finiteNum(value: unknown): number | null {
  // Number(null) === 0 — never coerce null/undefined/"" into a fake price.
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function zoneFromPair(
  highRaw: unknown,
  lowRaw: unknown,
  label: string
): StrategyRdMetaZone | null {
  const high = finiteNum(highRaw);
  const low = finiteNum(lowRaw);
  if (high == null || low == null) return null;
  const top = Math.max(high, low);
  const bottom = Math.min(high, low);
  if (!(top > bottom)) return null;
  return { high: top, low: bottom, mid: (top + bottom) / 2, label };
}

function asTimeString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

/** Stage analysis family (agnostic `stage` + legacy stage_2 / stage_4). */
export function strategyRdIsStageSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  return key === "stage" || key === "stage_2" || key === "stage_4" || /^stage_\d+$/.test(key);
}

export type StrategyRdStageSetupGeometry = {
  signalMode: "breakout" | "regime";
  stage: number | null;
  priorStage: number | null;
  level: number | null;
  levelSource: string | null;
  fromBarTime: string | null;
  toBarTime: string | null;
  boxHigh: number | null;
  boxLow: number | null;
  baseBars: number | null;
  barTime: string | null;
  maValue: number | null;
  maLength: number | null;
};

export type StrategyRdStageSegment = {
  stage: number | null;
  label: string;
  fromBarTime: string;
  toBarTime: string;
  high: number | null;
  low: number | null;
  bars: number | null;
};

/**
 * Stage analysis match geometry — BE fields only.
 * Do not invent base spans / levels from chart OHLC.
 */
export function extractStrategyRdStageSetupGeometry(
  meta: StrategyRdMatchMeta | null | undefined
): StrategyRdStageSetupGeometry | null {
  if (!meta) return null;
  const modeRaw = String(meta.signal_mode ?? meta.signalMode ?? "")
    .trim()
    .toLowerCase();
  const signalMode: "breakout" | "regime" =
    modeRaw === "regime" ? "regime" : "breakout";
  const stageRaw = finiteNum(meta.stage);
  const priorStage = finiteNum(meta.prior_stage ?? meta.priorStage);
  const level =
    finiteNum(meta.level) ??
    finiteNum(meta.break_level) ??
    finiteNum(meta.breakLevel);
  const levelSourceTag = (() => {
    const raw = meta.level_source ?? meta.levelSource;
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    return null;
  })();
  const barTime =
    asTimeString(meta.bar_time) ??
    asTimeString(meta.signal_bar_time) ??
    asTimeString(meta.break_bar_time);
  const maValue =
    finiteNum(meta.ma_value) ??
    finiteNum(meta.sma_value) ??
    finiteNum(meta.maValue);
  const maLength = finiteNum(meta.ma_length) ?? finiteNum(meta.maLength);

  // Breakout base box — times + pattern box from BE only.
  const fromBarTime =
    asTimeString(meta.base_start_time) ??
    asTimeString(meta.base_start_bar_time) ??
    asTimeString(meta.pattern_start_time) ??
    asTimeString(meta.pattern_start_bar_time) ??
    asTimeString(meta.from_bar_time);
  const toBarTime =
    asTimeString(meta.base_end_time) ??
    asTimeString(meta.base_end_bar_time) ??
    asTimeString(meta.pattern_end_time) ??
    asTimeString(meta.pattern_end_bar_time) ??
    asTimeString(meta.to_bar_time);

  let boxHigh =
    finiteNum(meta.pattern_high) ?? finiteNum(meta.patternHigh);
  let boxLow =
    finiteNum(meta.pattern_low) ?? finiteNum(meta.patternLow);
  if (boxHigh != null && boxLow != null && boxHigh < boxLow) {
    const tmp = boxHigh;
    boxHigh = boxLow;
    boxLow = tmp;
  }

  const baseBarsRaw = meta.base_bars ?? meta.baseBars;
  const baseBars =
    typeof baseBarsRaw === "number" && Number.isFinite(baseBarsRaw)
      ? baseBarsRaw
      : Number.isFinite(Number(baseBarsRaw))
        ? Number(baseBarsRaw)
        : null;

  if (
    signalMode === "regime" &&
    stageRaw == null &&
    !barTime &&
    maValue == null
  ) {
    return null;
  }
  if (
    signalMode === "breakout" &&
    level == null &&
    !fromBarTime &&
    !toBarTime &&
    !barTime &&
    maValue == null
  ) {
    return null;
  }

  return {
    signalMode,
    stage: stageRaw,
    priorStage,
    level: signalMode === "breakout" ? level : null,
    levelSource: signalMode === "breakout" ? levelSourceTag : null,
    fromBarTime: signalMode === "breakout" ? fromBarTime : null,
    toBarTime: signalMode === "breakout" ? toBarTime : null,
    boxHigh: signalMode === "breakout" ? boxHigh : null,
    boxLow: signalMode === "breakout" ? boxLow : null,
    baseBars: signalMode === "breakout" ? baseBars : null,
    barTime,
    maValue,
    maLength,
  };
}

/** BE `meta.stage_segments[]` — ≤4 trailing runs for this hit (prior→current story). Draw as-is. */
export function extractStrategyRdStageSegments(
  meta: StrategyRdMatchMeta | null | undefined
): StrategyRdStageSegment[] {
  if (!meta) return [];
  const raw = meta.stage_segments ?? meta.stageSegments;
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdStageSegment[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object" || Array.isArray(row)) continue;
    const rec = row as Record<string, unknown>;
    const fromBarTime =
      asTimeString(rec.from_bar_time) ?? asTimeString(rec.fromBarTime);
    const toBarTime =
      asTimeString(rec.to_bar_time) ?? asTimeString(rec.toBarTime);
    if (!fromBarTime || !toBarTime) continue;
    const stage = finiteNum(rec.stage);
    const labelRaw =
      typeof rec.label === "string" && rec.label.trim()
        ? rec.label.trim()
        : stage != null
          ? `Stage ${Math.round(stage)}`
          : "Stage";
    let high = finiteNum(rec.pattern_high) ?? finiteNum(rec.patternHigh);
    let low = finiteNum(rec.pattern_low) ?? finiteNum(rec.patternLow);
    if (high != null && low != null && high < low) {
      const tmp = high;
      high = low;
      low = tmp;
    }
    const barsRaw = rec.bars;
    const bars =
      typeof barsRaw === "number" && Number.isFinite(barsRaw)
        ? barsRaw
        : Number.isFinite(Number(barsRaw))
          ? Number(barsRaw)
          : null;
    out.push({
      stage,
      label: labelRaw,
      fromBarTime,
      toBarTime,
      high,
      low,
      bars,
    });
  }
  return out;
}

export type StrategyRdStageMarkerKind =
  | "base"
  | "breakout"
  | "breakdown"
  | "stage_flip"
  | "other";

export type StrategyRdStageDrawMarker = {
  kind: StrategyRdStageMarkerKind;
  label: string;
  barTime: string;
  price: number | null;
};

function normalizeStageMarkerKind(raw: unknown): StrategyRdStageMarkerKind | null {
  const key = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!key) return null;
  if (
    key === "stage_base_start" ||
    key === "base" ||
    key === "base_start" ||
    key === "base_end"
  ) {
    return "base";
  }
  if (key === "stage_break" || key === "breakout" || key === "break_out") {
    return "breakout";
  }
  if (key === "breakdown" || key === "break_down") return "breakdown";
  if (
    key === "stage_flip" ||
    key === "regime" ||
    key === "regime_flip" ||
    key.includes("stage_flip")
  ) {
    return "stage_flip";
  }
  // Label fallbacks
  if (key === "base") return "base";
  if (key.includes("breakout")) return "breakout";
  if (key.includes("breakdown")) return "breakdown";
  return null;
}

function stageMarkerLabel(
  kind: StrategyRdStageMarkerKind,
  fallbackLabel: string | null,
  stage: number | null
): string {
  const fromBe = (fallbackLabel || "").trim();
  if (fromBe) return fromBe;
  if (kind === "base") return "Base";
  if (kind === "breakout") return "Breakout";
  if (kind === "breakdown") return "Breakdown";
  if (kind === "stage_flip") {
    if (stage === 2) return "Stage 2";
    if (stage === 4) return "Stage 4";
    return "Stage flip";
  }
  return "Stage";
}

/**
 * BE `meta.markers[]` for Stage.
 * Regime → stage_flip only. Breakout → Base + Breakout/Breakdown.
 */
export function strategyRdStageDrawMarkers(
  meta: StrategyRdMatchMeta | null | undefined,
  opts?: { signalMode?: "breakout" | "regime" | null }
): StrategyRdStageDrawMarker[] {
  if (!meta) return [];
  const stage = finiteNum(meta.stage);
  const mode =
    opts?.signalMode ??
    (String(meta.signal_mode ?? "")
      .trim()
      .toLowerCase() === "regime"
      ? "regime"
      : "breakout");
  const markersRaw = meta.markers;
  const out: StrategyRdStageDrawMarker[] = [];
  if (Array.isArray(markersRaw)) {
    for (const row of markersRaw) {
      if (!row || typeof row !== "object" || Array.isArray(row)) continue;
      const rec = row as Record<string, unknown>;
      let kind =
        normalizeStageMarkerKind(rec.kind) ??
        normalizeStageMarkerKind(rec.label) ??
        normalizeStageMarkerKind(rec.type) ??
        normalizeStageMarkerKind(rec.name);
      const barTime =
        asTimeString(rec.bar_time) ??
        asTimeString(rec.time) ??
        asTimeString(rec.timestamp);
      if (!barTime) continue;
      const labelRaw =
        typeof rec.label === "string"
          ? rec.label
          : typeof rec.name === "string"
            ? rec.name
            : null;
      // stage_break + Breakdown label → breakdown kind
      if (
        kind === "breakout" &&
        String(labelRaw || "")
          .toLowerCase()
          .includes("breakdown")
      ) {
        kind = "breakdown";
      }
      const resolvedKind = kind ?? "other";
      if (mode === "regime" && resolvedKind !== "stage_flip") continue;
      if (
        mode === "breakout" &&
        resolvedKind !== "base" &&
        resolvedKind !== "breakout" &&
        resolvedKind !== "breakdown"
      ) {
        continue;
      }
      out.push({
        kind: resolvedKind,
        label: stageMarkerLabel(resolvedKind, labelRaw, stage),
        barTime,
        price:
          finiteNum(rec.price) ??
          finiteNum(rec.level) ??
          finiteNum(rec.ma_value) ??
          null,
      });
    }
  }
  return out;
}

/** Setup coil_range_contract: full contraction span + break box (last bar of run). */
export function extractStrategyRdCoilSetupSpan(
  meta: StrategyRdMatchMeta | null | undefined
): {
  fromBarTime: string;
  toBarTime: string;
  high: number;
  low: number;
  coilBars: number | null;
} | null {
  if (!meta) return null;
  const high = finiteNum(meta.level_high);
  const low = finiteNum(meta.level_low);
  const fromBarTime =
    asTimeString(meta.coil_start_bar_time) ??
    asTimeString(meta.from_bar_time) ??
    asTimeString(meta.start_bar_time);
  const toBarTime =
    asTimeString(meta.coil_end_bar_time) ??
    asTimeString(meta.to_bar_time) ??
    asTimeString(meta.end_bar_time) ??
    asTimeString(meta.bar_time);
  if (high == null || low == null || !(high > low) || !fromBarTime || !toBarTime) {
    return null;
  }
  const coilBarsRaw = meta.coil_bars ?? meta.coil_bars_count;
  const coilBars =
    typeof coilBarsRaw === "number" && Number.isFinite(coilBarsRaw)
      ? coilBarsRaw
      : Number.isFinite(Number(coilBarsRaw))
        ? Number(coilBarsRaw)
        : null;
  return {
    fromBarTime,
    toBarTime,
    high: Math.max(high, low),
    low: Math.min(high, low),
    coilBars,
  };
}

/** Order-block setup ids (agnostic + concretes). Not `near_ob` location. */
export function strategyRdIsOrderBlockSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key || key === "near_ob") return false;
  if (key.includes("order_block")) return true;
  // ob, ob_bull, ob_bear — not "near_ob" / "choch" etc.
  return key === "ob" || key.startsWith("ob_");
}

/**
 * High/low level-pair setups — draw as horizontal lines (DH/DL, PDH/PDL, PWH/PWL, …),
 * never a vertical rectangle between the extremes.
 */
export function strategyRdIsLevelPairSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  if (key === "developing_day_hl" || key.includes("developing_day")) return true;
  if (key === "pdh_pdl" || key.includes("previous_day")) return true;
  if (key.includes("pdh") && key.includes("pdl")) return true;
  if (key === "pwh_pwl" || (key.includes("pwh") && key.includes("pwl"))) return true;
  if (key === "pmh_pml" || (key.includes("pmh") && key.includes("pml"))) return true;
  if (key === "swing_hl" || key === "swing_high_low") return true;
  return false;
}

/**
 * OB setup geometry: body from ob_high/ob_low (also zone_* / level_* mirrors).
 * Prefer this over fvg_* / path levels — BE often attaches the displacement FVG
 * on the same match, which would float the box off the origin candle.
 * Span: from ob_bar_time forward through impulse (at least) — not a one-candle box.
 */
export function extractStrategyRdOrderBlockSetupZone(
  meta: StrategyRdMatchMeta | null | undefined
): (StrategyRdMetaZone & {
  fromBarTime: string | null;
  toBarTime: string | null;
}) | null {
  if (!meta) return null;
  const nested =
    asRecord(meta.order_block) ||
    asRecord(meta.ob) ||
    asRecord(meta.setup) ||
    asRecord(meta.zone) ||
    asRecord(meta.location);
  const zone =
    zoneFromPair(meta.ob_high, meta.ob_low, "Order block") ||
    zoneFromPair(meta.zone_high, meta.zone_low, "Order block") ||
    zoneFromPair(meta.level_high, meta.level_low, "Order block") ||
    zoneFromPair(nested?.ob_high, nested?.ob_low, "Order block") ||
    zoneFromPair(nested?.zone_high, nested?.zone_low, "Order block") ||
    zoneFromPair(nested?.high, nested?.low, "Order block");
  if (!zone) return null;
  const fromBarTime =
    asTimeString(meta.ob_bar_time) ??
    asTimeString(meta.from_bar_time) ??
    asTimeString(meta.bar_time) ??
    asTimeString(nested?.ob_bar_time) ??
    asTimeString(nested?.from_bar_time) ??
    asTimeString(nested?.bar_time);
  const toBarTime =
    asTimeString(meta.to_bar_time) ??
    asTimeString(meta.impulse_bar_time) ??
    asTimeString(meta.mss_bar_time) ??
    asTimeString(nested?.to_bar_time) ??
    asTimeString(nested?.impulse_bar_time) ??
    asTimeString(nested?.mss_bar_time);
  return { ...zone, fromBarTime, toBarTime };
}

/** Breaker block setup (agnostic + concretes). Not `near_breaker` location. */
export function strategyRdIsBreakerSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  return key === "breaker" || key.startsWith("breaker_");
}

/**
 * Breaker setup geometry: zone = origin OB body (flipped role);
 * signal / label bar = failure bar when the OB breaks.
 */
export function extractStrategyRdBreakerSetupZone(
  meta: StrategyRdMatchMeta | null | undefined
): (StrategyRdMetaZone & {
  failBarTime: string | null;
  fromBarTime: string | null;
  toBarTime: string | null;
}) | null {
  if (!meta) return null;
  const nested =
    asRecord(meta.breaker) ||
    asRecord(meta.setup) ||
    asRecord(meta.zone) ||
    asRecord(meta.location);
  const zone =
    zoneFromPair(meta.breaker_high, meta.breaker_low, "Breaker") ||
    zoneFromPair(meta.ob_high, meta.ob_low, "Breaker") ||
    zoneFromPair(nested?.breaker_high, nested?.breaker_low, "Breaker") ||
    zoneFromPair(nested?.ob_high, nested?.ob_low, "Breaker") ||
    zoneFromPair(nested?.high, nested?.low, "Breaker");
  if (!zone) return null;
  const failBarTime =
    asTimeString(meta.fail_bar_time) ??
    asTimeString(meta.failure_bar_time) ??
    asTimeString(nested?.fail_bar_time) ??
    asTimeString(nested?.failure_bar_time);
  const fromBarTime =
    asTimeString(meta.from_bar_time) ??
    asTimeString(meta.origin_ob_bar_time) ??
    asTimeString(nested?.from_bar_time) ??
    asTimeString(nested?.origin_ob_bar_time);
  const toBarTime =
    asTimeString(meta.to_bar_time) ??
    asTimeString(nested?.to_bar_time) ??
    failBarTime;
  return { ...zone, failBarTime, fromBarTime, toBarTime };
}

/** Extract a drawable zone/band from Concepts match meta. */
export function extractStrategyRdMetaZone(
  meta: StrategyRdMatchMeta | null | undefined
): StrategyRdMetaZone | null {
  if (!meta) return null;
  const nested =
    asRecord(meta.fvg) ||
    asRecord(meta.gap) ||
    asRecord(meta.location) ||
    asRecord(meta.setup) ||
    asRecord(meta.zone);
  return (
    zoneFromPair(meta.fvg_high, meta.fvg_low, "FVG") ||
    zoneFromPair(nested?.high ?? nested?.fvg_high, nested?.low ?? nested?.fvg_low, "FVG") ||
    zoneFromPair(meta.orb_high, meta.orb_low, "ORB") ||
    zoneFromPair(meta.ob_high, meta.ob_low, "Order block") ||
    zoneFromPair(meta.breaker_high, meta.breaker_low, "Breaker") ||
    zoneFromPair(
      nested?.breaker_high ?? nested?.ob_high,
      nested?.breaker_low ?? nested?.ob_low,
      "Breaker"
    ) ||
    zoneFromPair(
      meta.level_high,
      meta.level_low,
      meta.coil_start_bar_time != null || meta.coil_end_bar_time != null
        ? "Coil range"
        : "Level range"
    ) ||
    zoneFromPair(meta.mother_high, meta.mother_low, "Mother bar") ||
    // Fractal swing H/L (BOS/CHoCH/sweep/run) — not PDH/PWH calendar rails.
    zoneFromPair(meta.swing_high, meta.swing_low, "Swing") ||
    zoneFromPair(meta.pdh, meta.pdl, "PDH/PDL") ||
    zoneFromPair(meta.session_high, meta.session_low, "Session H–L") ||
    (() => {
      const level = finiteNum(meta.level);
      if (level == null) return null;
      return { high: level, low: level, mid: level, label: "Level" };
    })()
  );
}

export type { StrategyRdSessionsPayload };
